<template>
    <section class="py-10 md:py-20">
        <div class="container md:flex">
            <form class="fui-i block flex-1" method="post" @submit="onSubmit">
                <slot></slot>

                <input v-if="stripe" type="hidden" name="gatewayId" :value="gatewayId">
                <input type="hidden" name="orderEmail" :value="email">

                <div v-if="step === 'address'">
                    <div>
                        <h2 class="h2 mb-4">Shipping Address</h2>

                        <CheckoutNewAddress
                            v-model="shippingAddress"
                            class="mt-2"
                            type="shippingAddress"
                        />
                    </div>

                    <div class="mt-8">
                        <h2 class="h2 mb-4">Billing Address</h2>

                        <div class="mb-4">
                            <div class="fui-checkbox">
                                <input
                                    id="billingSameAsShipping"
                                    v-model="billingSameAsShipping"
                                    type="checkbox"
                                    name="billingSameAsShipping"
                                >

                                <label for="billingSameAsShipping">Use Shipping Address</label>
                            </div>
                        </div>

                        <div v-if="!billingSameAsShipping">
                            <CheckoutNewAddress
                                v-model="billingAddress"
                                class="mt-2"
                                type="billingAddress"
                            />
                        </div>
                    </div>

                    <div class="flex justify-end mt-8">
                        <button
                            class="btn btn-primary"
                            :class="{ loading }"
                            :disabled="loading || items.length === 0"
                        >
                            {{ hasShippingOptions ? 'Proceed to shipping' : 'Proceed to payment' }}
                        </button>
                    </div>
                </div>

                <div v-if="step === 'shipping'">
                    <h2 class="h2 mb-4">Shipping</h2>

                    <ul>
                        <li v-for="method in shippingMethods" :key="method.id">
                            <CheckoutShipping v-model="shippingMethodHandle" :method="method" />
                        </li>
                    </ul>

                    <div class="flex justify-between mt-8">
                        <button
                            type="button"
                            class="btn btn-simple btn-no-icon"
                            :disabled="loading"
                            @click.prevent="step = 'address'"
                        >
                            Shipping/billing address
                        </button>

                        <button
                            class="btn btn-primary"
                            :class="{ loading }"
                            :disabled="loading"
                        >
                            Proceed to payment
                        </button>
                    </div>
                </div>

                <div v-if="step === 'payment'">
                    <h2 class="h2 mb-4">Payment</h2>

                    <LabeledInput
                        v-model="email"
                        label="Email"
                        type="email"
                        name="email"
                        required
                        :disabled="cartLoading"
                        class="px-0 mb-4"
                        @change="onSaveEmail"
                    />

                    <div class="mb-4">
                        <div class="fui-checkbox">
                            <input
                                id="field-opt-out"
                                v-model="optOut"
                                type="checkbox"
                                name="fields[optOut]"
                            >

                            <label for="field-opt-out">I am not okay with receiving occasional marketing material</label>
                        </div>
                    </div>

                    <ul class="mb-6">
                        <li v-if="stripe">
                            <div class="checkout-radio relative group">
                                <input
                                    id="payment-method-stripe"
                                    v-model="gatewayId"
                                    type="radio"
                                    class="sr-only"
                                    :value="stripe.id"
                                    name="paymentMethod"
                                    required
                                >

                                <label for="payment-method-stripe" class="flex items-center p-2 bg-secondary-100 mb-2">
                                    <span class="checkout-radio__radio"></span>

                                    <span class="block flex-1 pr-10 text-sm leading-tight">Card</span>
                                </label>
                            </div>
                        </li>

                        <li v-if="paypal">
                            <div class="checkout-radio relative group">
                                <input
                                    id="payment-method-paypal"
                                    v-model="gatewayId"
                                    type="radio"
                                    class="sr-only"
                                    :value="paypal.id"
                                    name="paymentMethod"
                                    required
                                >

                                <label for="payment-method-paypal" class="flex items-center p-2 bg-secondary-100 mb-2">
                                    <span class="checkout-radio__radio"></span>

                                    <span class="block flex-1 pr-10 text-sm leading-tight">PayPal</span>
                                </label>
                            </div>
                        </li>
                    </ul>

                    <stripe
                        v-if="stripe && gatewayId === stripe.id"
                        :pk="stripe.pk"
                        :params="stripeParams"
                        @loading="loading = $event"
                    />

                    <paypal v-else-if="paypal && gatewayId === paypal.id" />

                    <div class="flex justify-between mt-8">
                        <button
                            type="button"
                            class="btn btn-simple btn-no-icon"
                            :disabled="loading"
                            @click.prevent="step = hasShippingOptions ? 'shipping' : 'address'"
                        >
                            {{ hasShippingOptions ? 'Shipping' : 'Shipping/billing address' }}
                        </button>

                        <button
                            v-if="stripe && gatewayId === stripe.id"
                            class="btn btn-primary"
                            :class="{ loading }"
                            :disabled="loading"
                        >
                            Checkout
                        </button>
                    </div>
                </div>
            </form>

            <div class="md:w-1/3 md:max-w-sm md:ml-20 to_md:mt-12">
                <h2 class="h2 flex items-center mb-4">
                    <span>Cart</span>
                    <span v-if="cartLoading" class="loading ml-6 mt-1" aria-hidden="true"></span>
                </h2>

                <div v-if="items.length">
                    <ul class="-mb-2">
                        <li v-for="line in items" :key="line.id">
                            <checkout-line :line="line" />
                        </li>
                    </ul>

                    <div class="mt-8">
                        <LabeledInput
                            v-model="voucherCode"
                            label="Coupon/Voucher"
                            type="text"
                            name="voucherCode"
                            :disabled="voucherLoading"
                            class="px-0 mb-4"
                            @change="onSaveVoucher"
                        />

                        <div v-if="voucherErrors.length > 0" class="alert alert-danger" role="alert">
                            <ul>
                                <li v-for="(voucherError, index) in voucherErrors" :key="index">{{ voucherError }}</li>
                            </ul>
                        </div>
                    </div>

                    <div class="mt-8">
                        <table class="w-full">
                            <tbody>
                                <tr>
                                    <td class="w-full">Subtotal</td>
                                    <td class="text-right">
                                        <MoneyFormat :amount="itemSubtotal" />
                                    </td>
                                </tr>

                                <tr v-for="adjustments in orderAdjustments" :key="adjustments.id">
                                    <td class="w-full">{{ adjustments.name }}</td>
                                    <td class="text-right">
                                        <MoneyFormat :amount="adjustments.amount" />
                                    </td>
                                </tr>

                                <tr v-if="totalDiscount < 0">
                                    <td class="w-full">Discount</td>
                                    <td class="text-right">
                                        <MoneyFormat :amount="totalDiscount" />
                                    </td>
                                </tr>

                                <tr>
                                    <td class="w-full h4 pt-4">Total</td>
                                    <td class="text-right pt-4">
                                        <MoneyFormat :amount="totalPrice" />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <p v-else>Your cart is empty.</p>
            </div>
        </div>
    </section>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import LabeledInput from '../LabeledInput.vue';
import MoneyFormat from '../MoneyFormat.vue';
import CheckoutLine from './CheckoutLine.vue';
import CheckoutNewAddress from './CheckoutNewAddress.vue';
import CheckoutShipping from './CheckoutShipping.vue';
import Paypal from './Paypal.vue';
import Stripe from './Stripe.vue';
const { mapActions, mapState } = createNamespacedHelpers('cart');

export default {
    components: {
        LabeledInput,
        MoneyFormat,
        CheckoutLine,
        CheckoutNewAddress,
        CheckoutShipping,
        Paypal,
        Stripe,
    },

    props: {
        initialStep: {
            type: String,
            default: 'address',
        },
    },

    data() {
        return {
            step: this.initialStep,
            loading: false,
            voucherLoading: false,
            voucherErrors: [],
            gatewayId: null,

            email: this.orderEmail,
            voucherCode: null,
            billingAddress: this.defaultAddress(),
            shippingAddress: this.defaultAddress(),
            shippingMethodHandle: null,
            billingSameAsShipping: true,
            optOut: false,
        };
    },

    computed: {
        ...mapState({
            items: 'items',
            orderAdjustments: 'orderAdjustments',
            addresses: 'addresses',
            orderEmail: 'email',
            itemSubtotal: 'itemSubtotal',
            totalShippingCost: 'totalShippingCost',
            totalDiscount: 'totalDiscount',
            totalPrice: 'totalPrice',
            stripe: 'stripe',
            paypal: 'paypal',
            cartLoading: 'loading',
            shippingMethods: 'shippingMethods',
            storeShippingMethodHandle: 'shippingMethodHandle',
            storeShippingAddress: 'shippingAddress',
            storeBillingAddress: 'billingAddress',
        }),
        hasShippingOptions() {
            return this.shippingMethods.length > 1 || (this.shippingMethods.length > 0 && !this.storeShippingMethod);
        },
        stripeParams() {
            return {
                ...this.stripe.params,
                email: this.email,
            };
        },
    },

    watch: {
        step(newValue) {
            if (!newValue || newValue === 'address') {
                history.replaceState(null, '', '/shop/checkout');
            } else {
                history.replaceState(null, '', `/shop/checkout/${newValue}`);
            }
        },
        items(newValue) {
            if (newValue.length === 0) {
                this.step = 'address';
            }
        },
        orderEmail(newValue) {
            this.email = newValue;
        },
        storeShippingMethodHandle(newValue) {
            this.shippingMethodHandle = newValue;
        },
        storeShippingAddress(newValue) {
            if (newValue) {
                this.shippingAddress = newValue;
                this.billingSameAsShipping = this.checkAddresses(newValue, this.billingAddress);
            }
        },
        storeBillingAddress(newValue) {
            if (newValue) {
                this.billingAddress = newValue;
                this.billingSameAsShipping = this.checkAddresses(this.shippingAddress, newValue);
            }
        },
    },

    methods: {
        ...mapActions([
            'updateCart',
            'loadCart',
        ]),

        defaultAddress() {
            return {
                fullName: null,
                addressLine1: null,
                addressLine2: null,
                locality: null,
                postalCode: null,
                countryCode: 'AU',
                administrativeArea: 'TAS',
                fields: {
                    phone: null,
                },
            };
        },

        checkAddresses(a, b) {
            if (!a || !b) {
                return false;
            }

            let same = true;

            for (const key in a) {
                if (Object.prototype.hasOwnProperty.call(a, key)) {
                    if (key === 'id') {
                        continue;
                    }

                    if (key === 'fields') {
                        same = this.checkAddresses(a[key], b[key]);
                        continue;
                    }

                    same = a[key] === b[key];
                }
            }

            return same;
        },

        onSubmit(event) {
            switch (this.step) {
                case 'address':
                    event.preventDefault();
                    event.stopPropagation();
                    this.onSaveAddress();
                    break;
                case 'shipping':
                    event.preventDefault();
                    event.stopPropagation();
                    this.onSaveShipping();
                    break;
            }
        },

        async onSaveAddress() {
            this.loading = true;

            const params = {
                shippingAddress: this.shippingAddress,
            };

            if (this.billingSameAsShipping) {
                params.billingAddress = this.shippingAddress;
            } else {
                params.billingAddress = this.billingAddress;
            }

            await this.updateCart(params);

            // this.shippingAddress = this.defaultAddress();
            // this.billingAddress = this.defaultAddress();

            this.loading = false;

            if (this.hasShippingOptions) {
                this.step = 'shipping';
            } else {
                this.step = 'payment';
            }
        },

        async onSaveShipping() {
            this.loading = true;

            const params = {
                shippingMethodHandle: this.shippingMethodHandle,
            };

            await this.updateCart(params);

            this.loading = false;
            this.step = 'payment';
        },

        async onSaveEmail() {
            this.loading = true;

            const params = {
                email: this.email,
            };

            await this.updateCart(params);

            this.loading = false;
        },

        async onSaveVoucher() {
            if (!this.voucherCode) {
                return;
            }

            this.voucherLoading = true;
            this.voucherErrors = [];

            const { data } = await this.$axios.post('/actions/gift-voucher/cart/add-code', {
                voucherCode: this.voucherCode,
            });

            if (data.errors?.voucherCode) {
                this.voucherErrors = data.errors.voucherCode;
            }

            await this.loadCart();

            this.voucherLoading = false;
        },
    },
};
</script>
